import { getOS } from '@dabble/quillui';
import { computed, ref } from 'vue';

interface ElectronBridge {
  desktop?: boolean;
  onMessage(fn: (name: string, data?: unknown) => void): void;
  send(name: string, data?: unknown): void;
}

declare global {
  interface Window {
    electron?: ElectronBridge;
  }
}

/**
 * The desktop shell loads accounts as a top-level navigation in its own window — login,
 * signup and billing all run inside the app — so its preload bridge is on the page here
 * exactly as it is in dabble-writer-3.0. Same probe.
 */
export const isDesktopApp = !!window.electron;

const isFullscreen = ref(false);

/**
 * The macOS shell is frameless (`titleBarStyle: 'hiddenInset'`): the window has no title
 * bar of its own and its traffic-light buttons float over the top-left of the page. So
 * accounts brings its own — a drag strip across the top of every page, with each page's
 * chrome held below it (see `@/styles/desktop-app.scss`). Native fullscreen hides the
 * lights and can't be dragged, so the strip goes away with them.
 */
export const needsTitleBar = computed(() => isDesktopApp && getOS() === 'mac' && !isFullscreen.value);

/** Track the shell's fullscreen state. No-op outside the desktop app. */
export function initDesktop() {
  window.electron?.onMessage((name, data) => {
    if (name === 'isFullscreen') isFullscreen.value = !!data;
  });
}
